import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { Check } from "lucide-react"
import { useSolveSignalsMutation } from "modules/dashboard/hooks/useSolveSignalsMutation"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

type Props = {
  signalIds: string[]
  isSolved: boolean
}

export function SolveSignalsButton({ signalIds, isSolved }: Props) {
  const { mutate: solveSignal, isPending } = useSolveSignalsMutation({
    onSuccess: (response) => {
      const allSolved = response.every((signal) => signal.isSolved)

      if (signalIds.length > 1) {
        toast.success(
          allSolved
            ? t("dashboard.signals.card.markedSolved")
            : t("dashboard.signals.card.markedUnsolved"),
        )
      } else {
        toast.success(
          allSolved
            ? t("dashboard.signals.card.markedSolved")
            : t("dashboard.signals.card.markedUnsolved"),
        )
      }
    },
  })

  const { t } = useTranslation()

  const copy = useMemo(() => {
    if (signalIds.length > 1) {
      if (isSolved) return t("dashboard.signals.card.markAllUnsolved")
      return t("dashboard.signals.card.markAllSolved")
    }

    if (isSolved) return t("dashboard.signals.card.markSolved")
    return t("dashboard.signals.card.markUnsolved")
  }, [isSolved, signalIds.length, t])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          disabled={isPending}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()

            solveSignal({ signalIds, isSolved: !isSolved })
          }}
        >
          <Check
            className={cn("size-5!", isSolved ? "text-foreground" : "text-foreground/30")}
            strokeWidth={2}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{copy}</TooltipContent>
    </Tooltip>
  )
}
